import { useNSRecommendationById } from '/@/hooks/nitrogen-smart';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import type { StageMessage } from './shared';
import { useProtectionActivity } from './use-protection-activity';

/**
 * "V5" banner — corn only, advisor-facing.
 *
 * Shown to advisor-type users when an active corn crop monitoring is in the V5 or V6 stage
 * (`phenology === 'emergence'` with `vegetativeStage` 5 or 6). Two messages, in priority order:
 *
 * 1. An NSmart recommendation exists but its refertilization date is unconfirmed → prompt to
 *    confirm the date so the technical team can process it. (`nitrogenRecomendations` on the CM
 *    payload is a list of ids, so the reco is fetched to read its `applicationDate`.)
 * 2. Otherwise, if no fungicide/insecticide application or recommendation is registered yet →
 *    the protection message (same as V2's second case / V3).
 *
 * Returns `null` outside V5/V6, for non-advisor users, or when neither message applies.
 */
export const useV5StageMessage = ({
    cropMonitoring,
    field,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();

    const corn = cropMonitoring?.cornCropMonitoring;
    const isCornV5 =
        Boolean(cropMonitoring?.active) &&
        cropMonitoring?.crop === 'corn' &&
        corn?.phenology === 'emergence' &&
        [5, 6].includes(corn?.vegetativeStage ?? 0);

    const showCandidate = isValidatedBayerUserType && isCornV5;

    // `nitrogenRecomendations` is a list of reco ids on the CM payload. Its presence tells us an
    // NSmart recommendation exists; we fetch the reco itself to read its confirmed date.
    const nsmartId = cropMonitoring?.nitrogenRecomendations?.[0] as number | undefined;
    const hasNSmart = nsmartId != null;
    const nsmartQuery = useNSRecommendationById(showCandidate && hasNSmart ? nsmartId : undefined);
    const nsmartReco = nsmartQuery.data as { applicationDate?: string } | undefined;

    const protection = useProtectionActivity({ cropMonitoring, field, enabled: showCandidate });

    if (!showCandidate) return null;

    // First message — an NSmart recommendation exists but its date is not yet confirmed. Wait for
    // the reco to load; a failed/absent reco falls through to the protection message rather than
    // wrongly prompting to confirm a date we couldn't read.
    if (hasNSmart) {
        if (nsmartQuery.isLoading) return null;
        if (nsmartReco && !nsmartReco.applicationDate) {
            return {
                title: _('Confirmá la fecha de refertilización'),
                message: _(
                    'La recomendación de NSmart ya está pedida. Confirmá la fecha de refertilización para que el equipo técnico procese los datos y devuelva la recomendación final.'
                ),
                actions: [{ label: _('Confirmar fecha'), variant: 'contained', action: 'confirm-nsmart-date' }],
            };
        }
    }

    // Second message — protect yield while no fungicide/insecticide activity is registered.
    if (protection.isLoading) return null;
    if (protection.hasActivity) return null;

    return {
        title: _('Ayudá a proteger el rendimiento del cultivo'),
        message: _(
            'Acompañá al productor a detectar plagas y enfermedades a tiempo y recomendá el producto para controlarlas.'
        ),
        actions: [
            { label: _('Registrar aplicación'), variant: 'contained', action: 'register-application' },
            { label: _('Generar recomendación'), variant: 'outlined', action: 'generate-cp-recommendation' },
        ],
    };
};
